import type { MetaFunction } from "@remix-run/cloudflare";
import { useEffect, useState } from "react";
import { Link } from "@remix-run/react";
import Footer from "~/components/Footer";
import { useScrollReveal } from "~/lib/use-scroll-reveal";
import styles from "~/styles/pageburn.module.css";

export const meta: MetaFunction = () => [
  { title: "PageBurn — Read Without Resistance" },
  { name: "description", content: "PageBurn is not about reading faster. It's about reading without the weight." },
];

const QUOTES = [
  { text: "The secret of change is to focus all your energy not on fighting the old, but on building the new.", author: "Socrates" },
  { text: "Not all those who wander are lost.", author: "Tolkien" },
  { text: "It is not death that a man should fear, but he should fear never beginning to live.", author: "Marcus Aurelius" },
  { text: "The man who does not value himself, cannot value anything or anyone.", author: "Ayn Rand" },
  { text: "A gilded cage is still a cage.", author: "Dostoevsky" },
  { text: "The question isn't who is going to let me; it's who is going to stop me.", author: "Ayn Rand" },
];

const FAQ = [
  { q: "Does it work on iOS and Android?", a: "PageBurn works in any modern browser — Chrome, Safari, Firefox. No install needed." },
  { q: "What happens to my texts?", a: "Your texts are yours. We don't store or share them." },
  { q: "Can I cancel anytime?", a: "Yes, no questions asked. Your PRO access stays active until the end of the paid period." },
  { q: "What's included in the first year updates?", a: "All new features released during your first PRO year are included at no extra cost." },
];

const TICKER_ITEMS = ["PageBurn", "·", "RSVP Reader", "·", "No Friction", "·", "Read Differently", "·", "Any Device", "·", "Any Text", "·"];

export default function PageBurnPage() {
  const [rsvpWord, setRsvpWord] = useState<{ left: string; center: string; right: string } | null>(null);
  const [rsvpSource, setRsvpSource] = useState("—");
  const [rsvpProgress, setRsvpProgress] = useState("——");
  const [brandVisible, setBrandVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    let qIdx = 0;
    let cancelled = false;
    let activeInterval: ReturnType<typeof setInterval> | undefined;
    const timers: number[] = [];

    function splitWord(word: string) {
      if (word.length <= 1) return { left: "", center: word, right: "" };
      const mid = Math.floor(word.length / 2);
      return { left: word.slice(0, mid), center: word[mid], right: word.slice(mid + 1) };
    }

    function showQuote(q: { text: string; author: string }) {
      if (cancelled) return;
      const words = q.text.split(" ");
      let wIdx = 0;
      setBrandVisible(false);
      setRsvpSource(q.author);

      const interval = setInterval(() => {
        if (cancelled) { clearInterval(interval); return; }
        if (wIdx < words.length) {
          setRsvpWord(splitWord(words[wIdx]));
          setRsvpProgress(Math.round((wIdx / words.length) * 100) + "%");
          wIdx++;
        } else {
          clearInterval(interval);
          setRsvpProgress("100%");
          timers.push(window.setTimeout(() => {
            if (cancelled) return;
            setRsvpWord(null);
            setBrandVisible(true);
            timers.push(window.setTimeout(() => {
              if (cancelled) return;
              setBrandVisible(false);
              timers.push(window.setTimeout(() => { if (cancelled) return; qIdx = (qIdx + 1) % QUOTES.length; showQuote(QUOTES[qIdx]); }, 400));
            }, 1200));
          }, 600));
        }
      }, 350);
      activeInterval = interval;
    }

    showQuote(QUOTES[0]);
    return () => {
      cancelled = true;
      if (activeInterval) clearInterval(activeInterval);
      timers.forEach(clearTimeout);
    };
  }, []);

  useScrollReveal();

  return (
    <div className={styles.pbTheme}>

      <section className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.blueprintWrap}>
          <div className={styles.blueprintFrame}>
            <div className={styles.bpHeader}>
              <div className={styles.bpHeaderDot} />
              <span className={styles.bpHeaderLabel}>PAGEBURN — v1.0</span>
              <span className={styles.bpHeaderSource}>{rsvpSource}</span>
            </div>
            <div className={styles.bpScreen}>
              <div className={styles.rsvpDisplay}>
                {rsvpWord ? (
                  <span className={styles.rsvpWord}>
                    {rsvpWord.left}<span style={{ color: "#FF6B00" }}>{rsvpWord.center}</span>{rsvpWord.right}
                  </span>
                ) : (
                  <span className={`${styles.rsvpBrand} ${brandVisible ? styles.rsvpBrandVisible : ""}`}>PAGEBURN</span>
                )}
              </div>
            </div>
            <div className={styles.bpFooter}>
              <span className={styles.bpFooterLabel}>PRODIGIUM DYNAMICS</span>
              <span className={styles.bpProgress}>{rsvpProgress}</span>
            </div>
          </div>
        </div>
        <h1 className={styles.heroTitle}>READ WITHOUT<br />RESISTANCE.</h1>
        <p className={styles.heroSub}>PageBurn is not about reading faster.<br />It&apos;s about reading without the weight.</p>
        <div className={styles.heroBtns}>
          <a href="https://pageburn.site" target="_blank" rel="noopener noreferrer" className="btn-primary">Try for free</a>
          <Link to="/pageburn/guide" className="btn-ghost">User Guide →</Link>
        </div>
      </section>

      <div className={styles.tickerThin}>
        <div className={styles.tickerThinTrack}>
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
            <span key={i} className={styles.tickerThinItem}>
              {t === "·" ? <span className={styles.tickerThinAccent}>✦</span> : t}
            </span>
          ))}
        </div>
      </div>

      <div className="rule" />

      <section id="manifesto" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>The PageBurn Manifesto</p>
        <div className={styles.statements}>
          {[
            { n: "01", title: "Reading should feel like thinking, not working.", text: "We were told that struggling through a text means we're being thorough. We disagree. Resistance is not depth — it's just friction." },
            { n: "02", title: "The problem was never speed. It was clarity.", text: "Speed reading tools tried to solve the wrong problem. PageBurn doesn't ask you to read faster — it removes the conditions that made reading feel slow in the first place." },
            { n: "03", title: "Your attention is not broken. It's misaligned.", text: "You don't have a focus problem. You have an environment problem. PageBurn is the environment designed around how your mind actually works." },
            { n: "04", title: "What you absorb matters more than what you finish.", text: "There is no trophy for finishing books you didn't understand. PageBurn is built for absorption — the kind that stays with you long after you've closed the page." },
            { n: "05", title: "Reading is not a skill you lack. It's a tool you deserve.", text: "PageBurn exists for every person who was told they weren't a reader. We built this for you specifically." },
          ].map(({ n, title, text }) => (
            <div key={n} className={`${styles.statement} reveal`}>
              <span className={styles.statementNum}>{n}</span>
              <div className={styles.statementText}>{title}<span>{text}</span></div>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section id="features" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>Features</p>
        <div className={`${styles.featuresGrid} reveal`}>
          {[
            { n: "01", title: "RSVP Native", desc: "Word by word at screen center. No eye movement, no friction." },
            { n: "02", title: "Adaptive Speed", desc: "Auto-slows on complex words. Read without effort." },
            { n: "03", title: "3 Devices", desc: "Your library synced across up to 3 devices." },
            { n: "04", title: "Eye Care", desc: "Automatic breaks. Designed for long sessions without fatigue." },
          ].map(({ n, title, desc }) => (
            <div key={n} className={styles.featureCard}>
              <div className={styles.featureIcon}>{n}</div>
              <h3 className={styles.featureTitle}>{title}</h3>
              <p className={styles.featureDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section id="pricing" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>Pricing</p>
        <div className={styles.pricingGrid}>
          <div className={`${styles.pricingCard} reveal`}>
            <div className={styles.pricingTier}>Free</div>
            <div className={styles.pricingPrice}>$0</div>
            <div className={styles.pricingPeriod}>forever</div>
            <ul className={styles.pricingFeatures}>
              <li>Paste text up to 2,500 characters</li>
              <li>Free ebooks included</li>
              <li>Native RSVP reader</li>
              <li>Basic speed control</li>
            </ul>
            <a href="https://pageburn.site" target="_blank" rel="noopener noreferrer" className={`${styles.pricingBtn} ${styles.pricingBtnGhost}`}>Start free →</a>
          </div>
          <div className={`${styles.pricingCard} ${styles.pricingCardPro} reveal`}>
            <div className={styles.pricingBadge}>Launch phase</div>
            <div className={styles.pricingTier}>PRO LIFETIME</div>
            <div className={styles.pricingPrice}>$12.90</div>
            <div className={styles.pricingPeriod}>
              <span className={styles.pricingOneTime}>One-time payment. Yours forever.</span>
              Early adopter pricing — limited.
              <span className={styles.pricingFuture}>Phase 2: $27.90</span>
            </div>
            <ul className={styles.pricingFeatures}>
              <li>Everything in Free</li>
              <li>PDF, TXT and MD file upload</li>
              <li>Library on 3 devices</li>
              <li>All skins and themes</li>
              <li>First year updates included</li>
              <li>Priority access to new features</li>
            </ul>
            <a href="https://whop.com/joined/pageburn/products/lifetime-early-adopter-access/" target="_blank" rel="noopener noreferrer" className={`${styles.pricingBtn} ${styles.pricingBtnPrimary}`}>Activate PRO</a>
          </div>
        </div>
      </section>

      <div className="rule" />

      <section id="faq" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>FAQ</p>
        <div className={styles.faqList}>
          {FAQ.map((item, i) => (
            <div key={i} className={styles.faqItem} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div className={styles.faqQ}>{item.q}<span className={styles.faqToggle}>{openFaq === i ? "−" : "+"}</span></div>
              {openFaq === i && <div className={styles.faqA}>{item.a}</div>}
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section className={styles.supportSection}>
        <h2 className={`${styles.supportTitle} reveal`}>Support</h2>
        <div className="reveal">
          <p className={styles.supportSub}>Questions, bugs, or just want to talk?<br />Real humans are behind PageBurn.</p>
          <a href="https://t.me/prodigiumdynamics" target="_blank" rel="noopener noreferrer" className={styles.btnTelegram}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.32 13.617l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.828.942z" />
            </svg>
            Contact via Telegram
          </a>
        </div>
      </section>

      <div className="rule" />

      <div className={styles.ctaBlock}>
        <h2 className={`${styles.ctaTitle} reveal`}>Ready to <span>burn</span> through it?</h2>
        <div className="reveal">
          <a href="https://pageburn.site" target="_blank" rel="noopener noreferrer" className="btn-primary">Open PageBurn</a>
        </div>
        <Link to="/pageburn/guide" className={styles.ctaGuideLink}>User Guide →</Link>
        <p className={styles.ctaSub}>A Prodigium Dynamics product — 2026</p>
      </div>

      <div className="rule" />
      <Footer />
    </div>
  );
}
